/*###########################################################################
 // Account page behaviours (safe across SPA navigations)
 //#########################################################################*/

import { showConfirm, showPrompt, showChecklist, bindConfirmForms } from './_confirmDialog'
import { showSiteToast, initSiteToast } from './_siteToast'
import { bindPokedexInfinite } from './_pokedexInfinite'

let importPollTimer = null
let importPollToken = 0

function cancelImportPoll() {
  if (importPollTimer) {
    window.clearTimeout(importPollTimer)
    importPollTimer = null
  }
  importPollToken += 1
}

function bindServerFlashes() {
  const dataEl = document.getElementById('site-flash-data')
  if (!dataEl || dataEl.dataset.consumed) return
  dataEl.dataset.consumed = '1'

  let payload = null
  try {
    payload = JSON.parse(dataEl.textContent || '{}')
  } catch {
    return
  }

  if (payload.error) {
    showSiteToast(payload.error)
  } else if (payload.notice) {
    showSiteToast(payload.notice)
  }
}

function bindCopyCardmarket() {
  const form = document.getElementById('tcg-cardmarket-export-form')
  const copyBtn = document.getElementById('tcg-copy-cardmarket')
  const downloadBtn = document.getElementById('tcg-download-cardmarket')
  const exportField = document.getElementById('tcg-cardmarket-export')
  const list = document.getElementById('tcg-cardmarket-card-list')
  const countEl = document.getElementById('tcg-cardmarket-selected-count')
  const selectAllBtn = document.getElementById('tcg-cardmarket-select-all')
  const selectNoneBtn = document.getElementById('tcg-cardmarket-select-none')

  if (!form || !copyBtn || !exportField || form.dataset.bound) return
  form.dataset.bound = '1'

  const checkboxes = () =>
    list ? [...list.querySelectorAll('input[type="checkbox"][data-export-line]')] : []

  const syncExport = () => {
    const selected = checkboxes().filter((box) => box.checked)
    const lines = selected
      .map((box) => box.dataset.exportLine || '')
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))

    exportField.value = lines.join('\n')
    if (countEl) countEl.textContent = String(selected.length)

    const hasSelection = selected.length > 0
    copyBtn.disabled = !hasSelection
    if (downloadBtn) downloadBtn.disabled = !hasSelection
  }

  list?.addEventListener('change', (e) => {
    if (e.target?.matches?.('input[type="checkbox"][data-export-line]')) {
      syncExport()
    }
  })

  selectAllBtn?.addEventListener('click', () => {
    checkboxes().forEach((box) => {
      box.checked = true
    })
    syncExport()
  })

  selectNoneBtn?.addEventListener('click', () => {
    checkboxes().forEach((box) => {
      box.checked = false
    })
    syncExport()
  })

  form.addEventListener('submit', (e) => {
    if (checkboxes().some((box) => box.checked)) return
    e.preventDefault()
    showSiteToast('Select at least one card to download.')
  })

  copyBtn.addEventListener('click', () => {
    const text = exportField.value
    if (!text) {
      showSiteToast('Select at least one card to copy.')
      return
    }

    const done = (ok) => {
      showSiteToast(
        ok
          ? 'Copied — paste into CardMarket’s Add Deck List.'
          : 'Couldn’t copy automatically — use Download instead.',
      )
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => done(true)).catch(() => done(false))
      return
    }

    exportField.classList.remove('sr-only')
    exportField.select()
    try {
      done(document.execCommand('copy'))
    } catch {
      done(false)
    }
    exportField.classList.add('sr-only')
  })

  syncExport()
}

function bindAjaxImportForm({ formId, runningLabel, doneLabel }) {
  const form = document.getElementById(formId)
  const statusUrl = form?.dataset.statusUrl

  if (!form || !statusUrl || form.dataset.bound) return
  form.dataset.bound = '1'

  form.addEventListener('submit', (e) => {
    e.preventDefault()
    const submitBtn = form.querySelector('button[type="submit"]')
    if (submitBtn) submitBtn.disabled = true

    cancelImportPoll()
    const token = importPollToken
    showSiteToast('Starting import…', { sticky: true })

    fetch(window.location.href, {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((r) => r.json())
      .then((data) => {
        if (token !== importPollToken) return
        if (!data.success) {
          showSiteToast(data.error || 'Import failed.')
          if (submitBtn) submitBtn.disabled = false
          return
        }
        poll(data.importId, token)
      })
      .catch(() => {
        if (token !== importPollToken) return
        showSiteToast('Import failed to start.')
        if (submitBtn) submitBtn.disabled = false
      })
  })

  function poll(importId, token) {
    if (token !== importPollToken) return

    const sep = statusUrl.includes('?') ? '&' : '?'
    fetch(`${statusUrl}${sep}importId=${encodeURIComponent(importId)}`, {
      headers: { Accept: 'application/json' },
    })
      .then((r) => r.json())
      .then((data) => {
        if (token !== importPollToken) return
        if (!data.found) {
          showSiteToast('Import not found.')
          return
        }
        if (data.status === 'running' || data.status === 'pending') {
          showSiteToast(runningLabel(data), { sticky: true })
          importPollTimer = window.setTimeout(() => poll(importId, token), 1500)
          return
        }
        if (data.status === 'failed') {
          showSiteToast(data.error || 'Import failed.')
          const submitBtn = form.querySelector('button[type="submit"]')
          if (submitBtn) submitBtn.disabled = false
          return
        }
        showSiteToast(doneLabel(data), { sticky: true })
        importPollTimer = window.setTimeout(() => {
          if (token === importPollToken) window.location.reload()
        }, 900)
      })
      .catch(() => {
        if (token !== importPollToken) return
        showSiteToast('Lost connection while importing.')
        const submitBtn = form.querySelector('button[type="submit"]')
        if (submitBtn) submitBtn.disabled = false
      })
  }
}

function updateCardsSummary(removedCount) {
  const summary = document.getElementById('cards-summary')
  if (!summary) return

  const cardCount = Math.max(0, (Number(summary.dataset.cardCount) || 0) - removedCount)
  const groupBySet = summary.dataset.groupBySet !== '0'
  const setCount = groupBySet
    ? document.querySelectorAll('#cards-by-set .js-set-panel').length
    : Number(summary.dataset.setCount) || 0

  summary.dataset.cardCount = String(cardCount)
  if (groupBySet) {
    summary.dataset.setCount = String(setCount)
  }
  summary.textContent = `${cardCount} card${cardCount === 1 ? '' : 's'} · ${setCount} set${setCount === 1 ? '' : 's'}`

  if (cardCount === 0) {
    summary.closest('.mt-8')?.remove()
  }
}

function portfolioIsEmpty() {
  return document.querySelectorAll('#cards-by-set .desk-card').length === 0
}

function clearEmptyPortfolio() {
  if (!portfolioIsEmpty()) return
  document.getElementById('cards-by-set')?.remove()
  document.getElementById('cards-summary')?.closest('[x-data]')?.remove()
}

function bindClearSetForms() {
  document.querySelectorAll('.js-clear-set-form').forEach((form) => {
    if (form.dataset.bound) return
    form.dataset.bound = '1'

    form.addEventListener('submit', async (e) => {
      e.preventDefault()

      const panel = form.closest('.js-set-panel')
      const setName = panel?.dataset.setName || 'this set'
      const cardCount = Number(panel?.dataset.cardCount) || 0
      const confirmMsg = `Remove all ${cardCount} card${cardCount === 1 ? '' : 's'} from ${setName}? Linked Vinted alerts will be deleted too.`

      const ok = await showConfirm({
        title: 'Clear this set?',
        message: confirmMsg,
        confirmLabel: 'Clear set',
      })
      if (!ok) return

      const btn = form.querySelector('button[type="submit"]')
      if (btn) {
        btn.disabled = true
        btn.textContent = 'Clearing…'
      }

      if (panel) {
        const height = panel.getBoundingClientRect().height
        panel.style.maxHeight = `${height}px`
        void panel.offsetHeight
        panel.classList.add('is-clearing')
      }

      showSiteToast(`Clearing ${setName}…`, { sticky: true })

      fetch(window.location.href, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then((r) => r.json())
        .then((data) => {
          if (!data.success) {
            if (panel) {
              panel.classList.remove('is-clearing')
              panel.style.maxHeight = ''
            }
            if (btn) {
              btn.disabled = false
              btn.textContent = 'Clear set'
            }
            showSiteToast(data.error || 'Couldn’t clear that set.')
            return
          }

          window.setTimeout(() => {
            panel?.remove()
            updateCardsSummary(Number(data.count) || cardCount)
            clearEmptyPortfolio()
            showSiteToast(data.message || `Removed cards from ${setName}.`)
          }, 320)
        })
        .catch(() => {
          if (panel) {
            panel.classList.remove('is-clearing')
            panel.style.maxHeight = ''
          }
          if (btn) {
            btn.disabled = false
            btn.textContent = 'Clear set'
          }
          showSiteToast('Lost connection while clearing the set.')
        })
    })
  })
}

function getSetFavoriteConfig() {
  const el = document.getElementById('set-favorite-config')
  if (!el) return null
  try {
    return JSON.parse(el.textContent || '{}')
  } catch {
    return null
  }
}

function reorderSetPanel(panel, favorited) {
  const list = document.getElementById('cards-by-set')
  if (!list || !panel) return

  if (favorited) {
    list.prepend(panel)
    return
  }

  const favoritedPanels = [...list.querySelectorAll('.js-set-panel[data-favorited="1"]')]
  const lastFavorite = favoritedPanels[favoritedPanels.length - 1]
  if (lastFavorite) {
    lastFavorite.after(panel)
  } else {
    list.append(panel)
  }
}

function getListManageConfig() {
  const el = document.getElementById('list-manage-config')
  if (!el) return null
  try {
    return JSON.parse(el.textContent || '{}')
  } catch {
    return null
  }
}

function reorderListPanel(panel, favorited) {
  const list = document.getElementById('cards-by-set')
  if (!list || !panel) return

  const unlisted = list.querySelector('.js-list-panel[data-unlisted="1"]')

  if (favorited) {
    list.prepend(panel)
    return
  }

  const favoritedPanels = [...list.querySelectorAll('.js-list-panel[data-favorited="1"][data-unlisted="0"]')]
  const lastFavorite = favoritedPanels[favoritedPanels.length - 1]
  if (lastFavorite) {
    lastFavorite.after(panel)
  } else if (unlisted) {
    unlisted.before(panel)
  } else {
    list.append(panel)
  }
}

function softRefreshCardsPage() {
  const url = `${window.location.pathname}${window.location.search}`
  const swup = window.swup
  if (swup?.navigate) {
    swup.cache?.delete?.(url)
    swup.hooks?.once?.('visit:start', (visit) => {
      if (visit?.scroll) visit.scroll.reset = false
    })
    // Bypass browser HTTP cache — a stale GET reverts group-by + CSRF after the first swap.
    swup.hooks?.once?.('fetch:request', (_visit, { options }) => {
      options.cache = 'no-store'
      options.headers = {
        ...(options.headers || {}),
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      }
    })
    swup.navigate(url, {
      animate: false,
      history: 'replace',
      cache: { read: false, write: false },
    })
    return
  }
  window.location.reload()
}

/** Group-by select on My Chases — save via AJAX, then Swup-swap the page. */
function bindCardsGroupBy() {
  // Delegate once so listeners survive Swup replacements.
  if (window.__cardsGroupByBound) return
  window.__cardsGroupByBound = true

  document.addEventListener('change', async (event) => {
    const select = event.target?.closest?.('[data-cards-group-by]')
    if (!select) return
    const form = select.closest('[data-cards-group-by-form]')
    if (!form || select.dataset.saving === '1') return

    const lastValue = select.dataset.savedValue || ''
    const next = select.value
    if (lastValue && next === lastValue) return

    // Build FormData before disabling — disabled fields are omitted from FormData,
    // which made the server fall back to cardsGroupBy=set on every toggle.
    const body = new FormData(form)
    body.set('cardsGroupBy', next)
    select.dataset.saving = '1'
    select.disabled = true
    const actionUrl = form.dataset.actionUrl || form.getAttribute('action') || window.location.href

    try {
      const res = await fetch(actionUrl, {
        method: 'POST',
        body,
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        cache: 'no-store',
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || data.success === false) {
        if (lastValue) select.value = lastValue
        select.disabled = false
        delete select.dataset.saving
        showSiteToast(data.error || 'Couldn’t update grouping.')
        return
      }
      select.dataset.savedValue = next
      softRefreshCardsPage()
    } catch {
      if (lastValue) select.value = lastValue
      select.disabled = false
      delete select.dataset.saving
      showSiteToast('Couldn’t update grouping.')
    }
  })
}

function bindListManage() {
  const cfg = getListManageConfig()

  document.querySelectorAll('[data-create-list]').forEach((btn) => {
    if (btn.dataset.bound) return
    btn.dataset.bound = '1'
    btn.addEventListener('click', async (e) => {
      e.preventDefault()
      if (!cfg?.createUrl) {
        showSiteToast('List config missing.')
        return
      }

      const name = await showPrompt({
        eyebrow: 'Lists',
        title: 'Create a list',
        message: 'Name a list to group chases. You can add cards when importing or from Add Card.',
        label: 'List name',
        placeholder: 'e.g. Master set chase',
        confirmLabel: 'Create list',
        cancelLabel: 'Cancel',
      })
      if (name == null) return

      btn.disabled = true
      const body = new FormData()
      body.append(cfg.csrfName || 'CRAFT_CSRF_TOKEN', cfg.csrfToken || '')
      body.append('name', name)

      try {
        const res = await fetch(cfg.createUrl, {
          method: 'POST',
          body,
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        })
        const data = await res.json()
        btn.disabled = false
        if (!data.success) {
          showSiteToast(data.error || 'Couldn’t create list.')
          return
        }
        showSiteToast(`Created “${data.list?.name || name}”.`)
        softRefreshCardsPage()
      } catch {
        btn.disabled = false
        showSiteToast('Couldn’t create list.')
      }
    })
  })

  document.querySelectorAll('[data-add-to-list]').forEach((btn) => {
    if (btn.dataset.bound) return
    btn.dataset.bound = '1'
    btn.addEventListener('click', async (e) => {
      e.preventDefault()
      e.stopPropagation()

      const userCardId = btn.dataset.userCardId
      const cardName = btn.dataset.cardName || 'this card'
      if (!userCardId || !cfg?.setMembershipUrl) {
        showSiteToast('List config missing.')
        return
      }

      let lists = Array.isArray(cfg.lists) ? [...cfg.lists] : []
      let selectedIds = null

      if (lists.length === 0) {
        if (!cfg.createUrl) {
          showSiteToast('Create a list first.')
          return
        }
        const name = await showPrompt({
          eyebrow: 'Lists',
          title: 'Create a list',
          message: `You don’t have any lists yet. Create one to add ${cardName}.`,
          label: 'List name',
          placeholder: 'e.g. Master set chase',
          confirmLabel: 'Create & add',
          cancelLabel: 'Cancel',
        })
        if (name == null) return

        btn.disabled = true
        try {
          const createBody = new FormData()
          createBody.append(cfg.csrfName || 'CRAFT_CSRF_TOKEN', cfg.csrfToken || '')
          createBody.append('name', name)
          const createRes = await fetch(cfg.createUrl, {
            method: 'POST',
            body: createBody,
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          })
          const createData = await createRes.json()
          if (!createData.success || !createData.list) {
            btn.disabled = false
            showSiteToast(createData.error || 'Couldn’t create list.')
            return
          }
          lists = [createData.list]
          cfg.lists = lists
          selectedIds = [String(createData.list.id)]
        } catch {
          btn.disabled = false
          showSiteToast('Couldn’t create list.')
          return
        }
      } else {
        selectedIds = await showChecklist({
          eyebrow: 'Lists',
          title: 'Add to list',
          message: `Choose one or more lists for ${cardName}.`,
          label: 'Your lists',
          items: lists.map((list) => ({
            value: String(list.id),
            label: list.name,
          })),
          confirmLabel: 'Add to list',
          cancelLabel: 'Cancel',
        })
        if (selectedIds == null) return
      }

      btn.disabled = true
      const body = new FormData()
      body.append(cfg.csrfName || 'CRAFT_CSRF_TOKEN', cfg.csrfToken || '')
      body.append('userCardId', userCardId)
      selectedIds.forEach((id) => body.append('listIds[]', id))

      try {
        const res = await fetch(cfg.setMembershipUrl, {
          method: 'POST',
          body,
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        })
        const data = await res.json()
        if (!data.success) {
          btn.disabled = false
          showSiteToast(data.error || 'Couldn’t update lists.')
          return
        }
        showSiteToast('Added to list.')
        softRefreshCardsPage()
      } catch {
        btn.disabled = false
        showSiteToast('Couldn’t update lists.')
      }
    })
  })

  if (!cfg?.toggleFavoriteUrl) return

  document.querySelectorAll('[data-list-favorite-toggle]').forEach((btn) => {
    if (btn.dataset.bound) return
    btn.dataset.bound = '1'

    btn.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()

      const panel = btn.closest('.js-list-panel')
      const listId = panel?.dataset.listId
      if (!panel || !listId || btn.disabled) return

      const next = panel.dataset.favorited !== '1'
      btn.disabled = true

      const body = new FormData()
      body.append(cfg.csrfName || 'CRAFT_CSRF_TOKEN', cfg.csrfToken || '')
      body.append('listId', listId)
      body.append('favorited', next ? '1' : '0')

      fetch(cfg.toggleFavoriteUrl, {
        method: 'POST',
        body,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then((r) => r.json())
        .then((data) => {
          btn.disabled = false
          if (!data.success) {
            showSiteToast(data.error || 'Couldn’t update favorite.')
            return
          }

          const favorited = !!data.favorited
          panel.dataset.favorited = favorited ? '1' : '0'
          panel.classList.toggle('is-favorited', favorited)
          btn.classList.toggle('is-active', favorited)
          btn.setAttribute('aria-pressed', favorited ? 'true' : 'false')
          btn.setAttribute('aria-label', favorited ? 'Remove list from favorites' : 'Favorite list')
          btn.setAttribute('title', favorited ? 'Unfavorite list' : 'Favorite list')
          const icon = btn.querySelector('span')
          if (icon) icon.textContent = favorited ? '★' : '☆'

          reorderListPanel(panel, favorited)
        })
        .catch(() => {
          btn.disabled = false
          showSiteToast('Couldn’t update favorite.')
        })
    })
  })

  document.querySelectorAll('[data-list-rename]').forEach((btn) => {
    if (btn.dataset.bound) return
    btn.dataset.bound = '1'

    btn.addEventListener('click', async (e) => {
      e.preventDefault()
      e.stopPropagation()

      const panel = btn.closest('.js-list-panel')
      const listId = panel?.dataset.listId
      const current = panel?.dataset.listName || ''
      if (!panel || !listId || !cfg.renameUrl) return

      const name = await showPrompt({
        eyebrow: 'Lists',
        title: 'Rename list',
        message: 'Choose a new name for this list.',
        label: 'List name',
        initialValue: current,
        confirmLabel: 'Save',
        cancelLabel: 'Cancel',
      })
      if (name == null) return
      const trimmed = name.trim()
      if (!trimmed || trimmed === current) return

      btn.disabled = true
      const body = new FormData()
      body.append(cfg.csrfName || 'CRAFT_CSRF_TOKEN', cfg.csrfToken || '')
      body.append('listId', listId)
      body.append('name', trimmed)

      try {
        const res = await fetch(cfg.renameUrl, {
          method: 'POST',
          body,
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        })
        const data = await res.json()
        btn.disabled = false
        if (!data.success) {
          showSiteToast(data.error || 'Couldn’t rename list.')
          return
        }
        panel.dataset.listName = data.list.name
        const title = panel.querySelector('.js-list-name')
        if (title) {
          const count = title.querySelector('.js-list-count')
          title.childNodes[0].textContent = `${data.list.name} `
          if (count) title.appendChild(count)
        }
        showSiteToast('List renamed.')
      } catch {
        btn.disabled = false
        showSiteToast('Couldn’t rename list.')
      }
    })
  })

  document.querySelectorAll('[data-list-delete]').forEach((btn) => {
    if (btn.dataset.bound) return
    btn.dataset.bound = '1'

    btn.addEventListener('click', async (e) => {
      e.preventDefault()
      e.stopPropagation()

      const panel = btn.closest('.js-list-panel')
      const listId = panel?.dataset.listId
      const name = panel?.dataset.listName || 'this list'
      if (!panel || !listId || !cfg.deleteUrl) return

      const ok = await showConfirm({
        title: 'Delete this list?',
        message: `Delete “${name}”? Cards stay on your chases; only the list is removed.`,
        confirmLabel: 'Delete list',
      })
      if (!ok) return

      btn.disabled = true
      const body = new FormData()
      body.append(cfg.csrfName || 'CRAFT_CSRF_TOKEN', cfg.csrfToken || '')
      body.append('listId', listId)

      try {
        const res = await fetch(cfg.deleteUrl, {
          method: 'POST',
          body,
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        })
        const data = await res.json()
        if (!data.success) {
          btn.disabled = false
          showSiteToast(data.error || 'Couldn’t delete list.')
          return
        }
        softRefreshCardsPage()
      } catch {
        btn.disabled = false
        showSiteToast('Couldn’t delete list.')
      }
    })
  })
}

function bindSetFavoriteToggles() {
  const cfg = getSetFavoriteConfig()
  if (!cfg?.toggleUrl) return

  document.querySelectorAll('[data-set-favorite-toggle]').forEach((btn) => {
    if (btn.dataset.bound) return
    btn.dataset.bound = '1'

    btn.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()

      const panel = btn.closest('.js-set-panel')
      const setName = panel?.dataset.setName
      if (!panel || !setName || btn.disabled) return

      const next = panel.dataset.favorited !== '1'
      btn.disabled = true

      const body = new FormData()
      body.append(cfg.csrfName || 'CRAFT_CSRF_TOKEN', cfg.csrfToken || '')
      body.append('setName', setName)
      body.append('favorited', next ? '1' : '0')

      fetch(cfg.toggleUrl, {
        method: 'POST',
        body,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then((r) => r.json())
        .then((data) => {
          btn.disabled = false
          if (!data.success) {
            showSiteToast(data.error || 'Couldn’t update favorite.')
            return
          }

          const favorited = !!data.favorited
          panel.dataset.favorited = favorited ? '1' : '0'
          panel.classList.toggle('is-favorited', favorited)
          btn.classList.toggle('is-active', favorited)
          btn.setAttribute('aria-pressed', favorited ? 'true' : 'false')
          btn.setAttribute('aria-label', favorited ? 'Remove set from favorites' : 'Favorite set')
          btn.setAttribute('title', favorited ? 'Unfavorite set' : 'Favorite set')
          const icon = btn.querySelector('span')
          if (icon) icon.textContent = favorited ? '★' : '☆'

          reorderSetPanel(panel, favorited)
          showSiteToast(favorited ? 'Set favorited — moved to top.' : 'Set removed from favorites.')
        })
        .catch(() => {
          btn.disabled = false
          showSiteToast('Lost connection while updating favorite.')
        })
    })
  })
}

function updateSetPanelCounts(panel, nextCount) {
  if (!panel) return

  panel.dataset.cardCount = String(nextCount)

  const countEl = panel.querySelector('.js-set-count')
  if (countEl) countEl.textContent = String(nextCount)

  const labelEl = panel.querySelector('.js-set-count-label')
  if (labelEl) {
    labelEl.textContent = `${nextCount} card${nextCount === 1 ? '' : 's'} in this set`
  }
}

function bindRemoveCardForms() {
  document.querySelectorAll('.js-remove-card-form').forEach((form) => {
    if (form.dataset.bound) return
    form.dataset.bound = '1'

    form.addEventListener('submit', async (e) => {
      e.preventDefault()

      const card = form.closest('.desk-card')
      const panel = form.closest('.js-set-panel')
      const btn = form.querySelector('button[type="submit"]')
      const cardName = card?.querySelector('h3')?.textContent?.trim() || 'Card'

      const ok = await showConfirm({
        title: 'Remove this card?',
        message: `Remove ${cardName} from your list? Linked Vinted alerts will be deleted too.`,
        confirmLabel: 'Remove',
      })
      if (!ok) return

      if (btn) {
        btn.disabled = true
        btn.textContent = 'Removing…'
      }

      fetch(window.location.href, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then((r) => r.json())
        .then((data) => {
          if (!data.success) {
            if (btn) {
              btn.disabled = false
              btn.textContent = 'Remove'
            }
            showSiteToast(data.error || 'Couldn’t remove that card.')
            return
          }

          card?.remove()

          const remainingInSet = panel ? panel.querySelectorAll('.desk-card').length : 0
          if (panel && remainingInSet === 0) {
            panel.remove()
          } else if (panel) {
            updateSetPanelCounts(panel, remainingInSet)
          }

          updateCardsSummary(1)
          clearEmptyPortfolio()

          showSiteToast(data.message || `Removed ${cardName}.`)
        })
        .catch(() => {
          if (btn) {
            btn.disabled = false
            btn.textContent = 'Remove'
          }
          showSiteToast('Lost connection while removing the card.')
        })
    })
  })
}

function csrfFieldsFrom(form) {
  const tokenInput =
    form?.querySelector('input[name="CRAFT_CSRF_TOKEN"]') ||
    form?.querySelector('input[name*="CSRF"]')

  if (tokenInput?.name && tokenInput.value) {
    return { name: tokenInput.name, value: tokenInput.value }
  }

  try {
    const cfg = JSON.parse(document.getElementById('add-card-picker-config')?.textContent || '{}')
    if (cfg.csrfName && cfg.csrfToken) {
      return { name: cfg.csrfName, value: cfg.csrfToken }
    }
  } catch {
    // ignore
  }

  return { name: 'CRAFT_CSRF_TOKEN', value: '' }
}

function redirectValueFrom(form) {
  return form?.querySelector('input[name="redirect"]')?.value || ''
}

function formActionFields(form) {
  const csrf = csrfFieldsFrom(form)
  return {
    csrfName: csrf.name,
    csrfValue: csrf.value,
    redirect: redirectValueFrom(form),
  }
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function formatAlertPrice(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '0.00'
  return n.toFixed(2)
}

const DISABLE_ALERT_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" aria-hidden="true">
	<path d="M6 6l12 12M18 6L6 18"/>
</svg>`

function postToggleAlert(form) {
  return fetch(window.location.href, {
    method: 'POST',
    body: new FormData(form),
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
  }).then((r) => r.json())
}

function clearAlertActions(actions) {
  if (!actions) return
  Array.from(actions.children).forEach((el) => {
    if (!el.classList.contains('desk-card__info-btn')) el.remove()
  })
}

function morphCardToAlerting(card, data, sourceForm = null) {
  if (!card) return

  const price = formatAlertPrice(data.maxPrice)
  const listingsUrl = data.listingsUrl || `/account/cards/${data.userCardId}/listings`
  const { csrfName, csrfValue, redirect } = formActionFields(
    sourceForm || card.querySelector('.js-add-alert-form, .js-disable-alert-form, form'),
  )

  let status = card.querySelector('.desk-card__alert-status')
  if (!status) {
    status = document.createElement('p')
    status.className = 'desk-card__alert-status'
    const media = card.querySelector('.desk-card__media')
    if (media) media.appendChild(status)
    else card.appendChild(status)
  }
  status.textContent = `≤ £${price}`

  const actions = card.querySelector('.desk-card__actions')
  clearAlertActions(actions)
  if (!actions) return

  // Keep Card Info as a peer control beside the armed alert actions (same 50/50 grid slot as Add Alert).
  const armed = document.createElement('div')
  armed.className = 'desk-card__alert-armed'
  armed.innerHTML = `
    <a class="btn btn--poke btn--small grow text-center" href="${escapeHtml(listingsUrl)}">View Listings</a>
    <form method="post" accept-charset="UTF-8" class="js-disable-alert-form inline-flex shrink-0">
      <input type="hidden" name="${escapeHtml(csrfName)}" value="${escapeHtml(csrfValue)}">
      <input type="hidden" name="action" value="tcgcollector/cards/toggle-alert">
      ${redirect ? `<input type="hidden" name="redirect" value="${escapeHtml(redirect)}">` : ''}
      <input type="hidden" name="userCardId" value="${escapeHtml(data.userCardId)}">
      <input type="hidden" name="enabled" value="0">
      <button type="submit" class="btn btn--circle btn--circle-danger" aria-label="Disable alert" title="Disable alert">
        ${DISABLE_ALERT_ICON}
      </button>
    </form>
  `
  actions.appendChild(armed)
  bindDisableAlertForms(armed)
}

function morphCardToIdle(card, data, sourceForm = null) {
  if (!card) return

  card.querySelector('.desk-card__alert-status')?.remove()

  const { csrfName, csrfValue, redirect } = formActionFields(
    sourceForm || card.querySelector('.js-disable-alert-form, .js-add-alert-form, form'),
  )
  const userCardId = data.userCardId || card.dataset.userCardId
  const maxPrice = data.maxPrice != null ? formatAlertPrice(data.maxPrice) : ''

  const actions = card.querySelector('.desk-card__actions')
  clearAlertActions(actions)
  if (!actions) return

  const fragment = document.createElement('div')
  fragment.innerHTML = `
    <button type="button" class="btn btn--outline btn--small w-full js-show-alert-form">Add Alert</button>
    <form method="post" accept-charset="UTF-8" class="desk-alert-form js-add-alert-form" hidden>
      <input type="hidden" name="${escapeHtml(csrfName)}" value="${escapeHtml(csrfValue)}">
      <input type="hidden" name="action" value="tcgcollector/cards/toggle-alert">
      ${redirect ? `<input type="hidden" name="redirect" value="${escapeHtml(redirect)}">` : ''}
      <input type="hidden" name="userCardId" value="${escapeHtml(userCardId)}">
      <input type="hidden" name="enabled" value="1">
      <label class="desk-alert-form__field">
        <span class="desk-alert-form__label">Max £</span>
        <input type="number" name="maxPrice" step="0.01" min="0.01" value="${escapeHtml(maxPrice)}" placeholder="0.00" required class="field-input" aria-label="Max price">
      </label>
      <button type="submit" class="btn btn--poke btn--small">Add Alert</button>
    </form>
  `
  while (fragment.firstChild) actions.appendChild(fragment.firstChild)

  bindShowAlertForms(actions)
  bindAddAlertForms(actions)
}

function bindShowAlertForms(root = document) {
  root.querySelectorAll('.js-show-alert-form').forEach((btn) => {
    if (btn.dataset.bound) return
    btn.dataset.bound = '1'

    btn.addEventListener('click', () => {
      const card = btn.closest('.desk-card')
      const form = card?.querySelector('.js-add-alert-form')
      btn.hidden = true
      if (form) {
        form.hidden = false
        form.querySelector('[name="maxPrice"]')?.focus()
      }
    })
  })
}

function bindAddAlertForms(root = document) {
  root.querySelectorAll('.js-add-alert-form').forEach((form) => {
    if (form.dataset.bound) return
    form.dataset.bound = '1'

    form.addEventListener('submit', (e) => {
      e.preventDefault()
      e.stopPropagation()

      const card = form.closest('.desk-card')
      const btn = form.querySelector('button[type="submit"]')
      const prevLabel = btn?.textContent

      if (btn) {
        btn.disabled = true
        btn.textContent = 'Adding…'
      }

      postToggleAlert(form)
        .then((data) => {
          if (!data.success) {
            if (btn) {
              btn.disabled = false
              btn.textContent = prevLabel || 'Add Alert'
            }
            showSiteToast(data.error || 'Couldn’t add that alert.')
            return
          }

          morphCardToAlerting(card, data, form)
          showSiteToast(data.message || 'Alert added.')
          window.dispatchEvent(new CustomEvent('app:walkthrough-task', { detail: { task: 'setFirstAlert' } }))
        })
        .catch(() => {
          if (btn) {
            btn.disabled = false
            btn.textContent = prevLabel || 'Add Alert'
          }
          showSiteToast('Lost connection while adding the alert.')
        })
    })
  })
}

function updateAlertsSummary(removed = 1) {
  const el = Array.from(document.querySelectorAll('p.text-sm.text-ink-muted.tabular-nums')).find((node) =>
    /active alert/i.test(node.textContent || ''),
  )
  if (!el) return

  const match = el.textContent?.match(/(\d+)/)
  if (!match) return

  const next = Math.max(0, Number(match[1]) - removed)
  el.textContent = `${next} active alert${next === 1 ? '' : 's'}`
}

function bindDisableAlertForms(root = document) {
  root.querySelectorAll('.js-disable-alert-form').forEach((form) => {
    if (form.dataset.bound) return
    form.dataset.bound = '1'

    form.addEventListener('submit', async (e) => {
      e.preventDefault()
      e.stopPropagation()

      const card = form.closest('.desk-card')
      const alertRow = form.closest('li')
      const btn = form.querySelector('button[type="submit"]')

      const ok = await showConfirm({
        title: 'Disable this alert?',
        message: card
          ? 'Stop watching Vinted for this card? You can re-enable the alert later.'
          : 'Stop watching Vinted for this card? You can re-enable the alert later from the card page.',
        confirmLabel: 'Disable',
      })
      if (!ok) return

      if (btn) btn.disabled = true

      postToggleAlert(form)
        .then((data) => {
          if (!data.success) {
            if (btn) btn.disabled = false
            showSiteToast(data.error || 'Couldn’t disable that alert.')
            return
          }

          if (card) {
            morphCardToIdle(card, data, form)
          } else if (alertRow) {
            const list = alertRow.parentElement
            alertRow.remove()
            updateAlertsSummary(1)
            if (list && list.querySelectorAll('li').length === 0) {
              window.location.reload()
              return
            }
          }

          window.dispatchEvent(
            new CustomEvent('app:alert-disabled', {
              detail: { userCardId: Number(data.userCardId) || null },
            }),
          )
          showSiteToast(data.message || 'Alert disabled.')
        })
        .catch(() => {
          if (btn) btn.disabled = false
          showSiteToast('Lost connection while disabling the alert.')
        })
    })
  })
}

/** Soft-swap the alert block on account/cards/<id> after add/disable. */
async function refreshCardAlertPanel() {
  const current = document.querySelector('[data-card-alert-panel]')
  if (!current) return false

  const res = await fetch(window.location.href, {
    headers: {
      Accept: 'text/html',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
    cache: 'no-store',
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)

  const html = await res.text()
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const next = doc.querySelector('[data-card-alert-panel]')
  if (!next) return false

  const Alpine = window.Alpine
  try {
    Alpine?.destroyTree?.(current)
  } catch {
    // ignore
  }

  const fresh = next.cloneNode(true)
  current.replaceWith(fresh)

  try {
    Alpine?.initTree?.(fresh)
  } catch {
    // ignore
  }

  return true
}

/** Card detail page — add/disable alert without a full reload. */
function bindCardDetailAlertPanel() {
  if (window.__cardDetailAlertBound) return
  window.__cardDetailAlertBound = true

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target
      if (!(form instanceof HTMLFormElement)) return
      if (!form.closest('[data-card-alert-panel]')) return

      const isAdd = form.classList.contains('js-card-detail-add-alert')
      const isDisable = form.classList.contains('js-card-detail-disable-alert')
      if (!isAdd && !isDisable) return

      event.preventDefault()
      event.stopPropagation()

      if (form.dataset.saving === '1') return

      if (isDisable) {
        const ok = await showConfirm({
          title: 'Disable this alert?',
          message: 'Stop watching Vinted for this card? You can re-enable the alert later.',
          confirmLabel: 'Disable',
        })
        if (!ok) return
      }

      const btn = form.querySelector('button[type="submit"]')
      const prevLabel = btn?.textContent
      form.dataset.saving = '1'
      if (btn) {
        btn.disabled = true
        if (isAdd) btn.textContent = 'Adding…'
      }

      try {
        const data = await postToggleAlert(form)
        if (!data.success) {
          if (btn) {
            btn.disabled = false
            if (prevLabel) btn.textContent = prevLabel
          }
          delete form.dataset.saving
          showSiteToast(data.error || (isAdd ? 'Couldn’t add that alert.' : 'Couldn’t disable that alert.'))
          return
        }

        const swapped = await refreshCardAlertPanel()
        if (!swapped) {
          softRefreshCardsPage()
          return
        }

        if (isAdd) {
          showSiteToast(data.message || 'Alert added.')
          window.dispatchEvent(new CustomEvent('app:walkthrough-task', { detail: { task: 'setFirstAlert' } }))
        } else {
          window.dispatchEvent(
            new CustomEvent('app:alert-disabled', {
              detail: { userCardId: Number(data.userCardId) || null },
            }),
          )
          showSiteToast(data.message || 'Alert disabled.')
        }
      } catch {
        if (btn) {
          btn.disabled = false
          if (prevLabel) btn.textContent = prevLabel
        }
        delete form.dataset.saving
        showSiteToast(isAdd ? 'Lost connection while adding the alert.' : 'Lost connection while disabling the alert.')
      }
    },
    true,
  )
}

function bindCardsPage() {
  bindCopyCardmarket()
  bindCardsGroupBy()
  bindClearSetForms()
  bindSetFavoriteToggles()
  bindListManage()
  bindRemoveCardForms()
  bindShowAlertForms()
  bindAddAlertForms()
  bindDisableAlertForms()

  bindAjaxImportForm({
    formId: 'tcg-import-form',
    runningLabel: (data) => {
      const found = Number(data.cardsFound) || 0
      const processed = Number(data.cardsProcessed) || 0
      const added = Number(data.cardsAdded) || 0
      if (!found) {
        return 'Fetching cards from TCG Collector…'
      }
      if (processed === 0) {
        return `Found ${found} card${found === 1 ? '' : 's'} — starting import…`
      }
      return `Importing… ${processed} of ${found} (${added} new)`
    },
    doneLabel: (data) => `Done — ${data.cardsAdded} new card(s) added. Reloading…`,
  })

  bindAjaxImportForm({
    formId: 'collectr-import-form',
    runningLabel: (data) => {
      const unmatched = data.cardsUnmatched ?? 0
      return `Matching… (${data.cardsAdded} added · ${unmatched} unmatched of ${data.cardsFound})`
    },
    doneLabel: (data) => {
      const unmatched = data.cardsUnmatched ?? 0
      return `Done — ${data.cardsAdded} added · ${unmatched} unmatched. Reloading…`
    },
  })
}

function bindAlertsPage() {
  bindDisableAlertForms()
}

function bindListingsPage() {
  const form = document.getElementById('tcg-fetch-listings-form')
  const btn = document.getElementById('tcg-fetch-listings-btn')
  if (!form || !btn || form.dataset.bound) return
  form.dataset.bound = '1'

  form.addEventListener('submit', () => {
    btn.disabled = true
    btn.textContent = 'Refreshing…'
    showSiteToast('Refreshing listings…', { sticky: true })
  })
}

function bindListAssignFields() {
  document.querySelectorAll('.js-list-assign').forEach((root) => {
    if (root.dataset.bound) return
    root.dataset.bound = '1'

    const createBtn = root.querySelector('.js-list-assign-create')
    const nameInput = root.querySelector('.js-list-assign-name')
    const extra = root.querySelector('.js-list-assign-extra')
    const createUrl = root.dataset.createUrl
    if (!createBtn || !nameInput || !createUrl) return

    const create = async () => {
      const name = nameInput.value.trim()
      if (!name || createBtn.disabled) return

      createBtn.disabled = true
      const body = new FormData()
      body.append(root.dataset.csrfName || 'CRAFT_CSRF_TOKEN', root.dataset.csrfToken || '')
      body.append('name', name)

      try {
        const res = await fetch(createUrl, {
          method: 'POST',
          body,
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        })
        const data = await res.json()
        createBtn.disabled = false
        if (!data.success || !data.list) {
          showSiteToast(data.error || 'Couldn’t create list.')
          return
        }

        const label = document.createElement('label')
        label.className = 'flex items-center gap-2 text-sm text-ink-soft cursor-pointer'
        label.innerHTML = `<input type="checkbox" name="listIds[]" value="${data.list.id}" class="shrink-0" checked><span></span>`
        label.querySelector('span').textContent = data.list.name
        ;(extra || root).appendChild(label)
        nameInput.value = ''
        showSiteToast(`Created “${data.list.name}”.`)
      } catch {
        createBtn.disabled = false
        showSiteToast('Couldn’t create list.')
      }
    }

    createBtn.addEventListener('click', (e) => {
      e.preventDefault()
      create()
    })
    nameInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        create()
      }
    })
  })
}

function bindFieldFiles() {
  document.querySelectorAll('.js-field-file').forEach((input) => {
    if (input.dataset.bound) return
    input.dataset.bound = '1'

    const root = input.closest('.field-file')
    const nameEl = root?.querySelector('.field-file__name')
    const emptyLabel = input.dataset.emptyLabel || 'No file chosen'

    const sync = () => {
      const file = input.files?.[0]
      if (!nameEl) return
      if (file) {
        nameEl.textContent = file.name
        root?.classList.add('is-chosen')
      } else {
        nameEl.textContent = emptyLabel
        root?.classList.remove('is-chosen')
      }
    }

    input.addEventListener('change', sync)
    sync()
  })
}

export function initAccountPages() {
  initSiteToast()

  const run = () => {
    // Leaving an import page mid-poll must not keep toasting / reloading.
    if (!document.getElementById('tcg-import-form') && !document.getElementById('collectr-import-form')) {
      cancelImportPoll()
    }
    bindServerFlashes()
    bindFieldFiles()
    bindListAssignFields()
    bindCardsPage()
    bindCardDetailAlertPanel()
    bindAlertsPage()
    bindListingsPage()
    bindConfirmForms()
    bindPokedexInfinite()
  }

  run()

  if (!window.__accountPagesNavBound) {
    window.__accountPagesNavBound = true
    window.addEventListener('app:navigated', run)
  }
}

export { showConfirm, showPrompt, showChecklist, showSiteToast }
